import { useState, useMemo, useEffect, useRef } from 'react';
import { Calendar, Clock, X } from 'lucide-react';

/**
 * Date+time picker for scheduling a notification send. Renders explicitly
 * in Sydney TZ (Australia/Sydney) regardless of the operator's machine TZ.
 *
 * The native `<input type="datetime-local">` is timezone-naive — it
 * accepts and returns wall-clock with NO timezone info. If the operator
 * is on a Sydney machine, that's fine; if they're travelling, the input
 * would silently interpret their entry in machine-local TZ. So we
 * disregard the browser's TZ entirely: we compute "now in Sydney" as the
 * default, accept whatever wall-clock the operator types as Sydney time,
 * and convert to a UTC ISO string before submitting.
 *
 * Used by both the per-draft action panel (Grouped view "Schedule…")
 * and the calendar block "Reschedule" action.
 */
interface ScheduleSendPopoverProps {
  /** Pre-fill date+time from this UTC ISO string (e.g. for reschedule). Null = next half-hour Sydney. */
  initialUtcIso?: string | null;
  /** Optional draft expires_at — picker rejects submissions on/after this. */
  expiresAtUtcIso?: string | null;
  /** Called with the chosen time as a UTC ISO string. */
  onSubmit: (utcIso: string) => void | Promise<void>;
  onClose: () => void;
  /** Disables Submit while a parent mutation is in flight. */
  submitting?: boolean;
}

/**
 * Format a UTC Date as wall-clock in Sydney TZ, returning the
 * components needed for the date+time inputs.
 */
function utcToSydneyParts(d: Date): { date: string; time: string } {
  // `en-CA` locale → YYYY-MM-DD; `en-GB` 24-hour → HH:MM:SS.
  const datePart = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Australia/Sydney',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
  const timePart = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Australia/Sydney',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
  return { date: datePart, time: timePart };
}

/**
 * Interpret `YYYY-MM-DD HH:MM` as Sydney wall-clock and return the
 * corresponding UTC Date. We rely on `Intl.DateTimeFormat` to compute
 * Sydney's UTC offset at that instant (handles AEST/AEDT switch).
 */
function sydneyWallClockToUtcDate(dateStr: string, timeStr: string): Date | null {
  if (!dateStr || !timeStr) return null;
  // Sydney wall-clock as if it were UTC — gives us a reference instant.
  const naiveUtc = new Date(`${dateStr}T${timeStr}:00Z`);
  if (Number.isNaN(naiveUtc.getTime())) return null;

  // What Sydney clock would read at `naiveUtc` if treated as UTC.
  const sydneyAtNaive = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Australia/Sydney',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
    .formatToParts(naiveUtc)
    .reduce<Record<string, string>>((acc, p) => {
      if (p.type !== 'literal') acc[p.type] = p.value;
      return acc;
    }, {});
  const sydneyAsInstant = Date.UTC(
    Number(sydneyAtNaive.year),
    Number(sydneyAtNaive.month) - 1,
    Number(sydneyAtNaive.day),
    Number(sydneyAtNaive.hour) % 24,
    Number(sydneyAtNaive.minute),
    Number(sydneyAtNaive.second),
  );
  // Offset from "treat as UTC" to actual Sydney instant.
  const offsetMs = sydneyAsInstant - naiveUtc.getTime();
  // The real UTC instant for the requested Sydney wall-clock.
  return new Date(naiveUtc.getTime() - offsetMs);
}

function nextHalfHourSydney(): Date {
  const now = new Date();
  const { date, time } = utcToSydneyParts(now);
  const [hh, mm] = time.split(':').map(Number);
  // Round forward to next :00 or :30.
  let newH = hh;
  let newM = mm < 30 ? 30 : 0;
  if (mm >= 30) newH = (hh + 1) % 24;
  const targetTime = `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
  // If we wrapped past midnight, advance the date one Sydney day.
  let targetDate = date;
  if (mm >= 30 && hh === 23) {
    const [y, mo, d] = date.split('-').map(Number);
    const nextLocal = new Date(Date.UTC(y, mo - 1, d + 1));
    targetDate = utcToSydneyParts(nextLocal).date;
  }
  const utc = sydneyWallClockToUtcDate(targetDate, targetTime);
  return utc ?? now;
}

export function ScheduleSendPopover({
  initialUtcIso,
  expiresAtUtcIso,
  onSubmit,
  onClose,
  submitting,
}: ScheduleSendPopoverProps) {
  const initial = useMemo(() => {
    if (initialUtcIso) {
      try {
        return utcToSydneyParts(new Date(initialUtcIso));
      } catch {
        // fall through
      }
    }
    return utcToSydneyParts(nextHalfHourSydney());
  }, [initialUtcIso]);

  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const utc = sydneyWallClockToUtcDate(date, time);
    if (!utc) {
      setError('Invalid date or time.');
      return;
    }
    if (utc.getTime() < Date.now() - 60_000) {
      setError('Scheduled time is in the past.');
      return;
    }
    if (expiresAtUtcIso) {
      const expiresAt = new Date(expiresAtUtcIso);
      if (utc.getTime() >= expiresAt.getTime()) {
        setError(`Must be before draft expiry (${utcToSydneyParts(expiresAt).date} ${utcToSydneyParts(expiresAt).time} AEST).`);
        return;
      }
    }
    await onSubmit(utc.toISOString());
  };

  return (
    <div
      ref={containerRef}
      className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-surface rounded-lg border border-grey-200 shadow-lg z-50 p-4"
      role="dialog"
      aria-label="Schedule send"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-primary-04" />
          <h3 className="text-sm font-semibold text-grey-700">Schedule send</h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-grey-500 hover:text-grey-700"
          aria-label="Close"
          disabled={submitting}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label htmlFor="sched-date" className="block text-xs font-medium text-grey-600 mb-1">
            Date
          </label>
          <div className="relative">
            <Calendar className="h-4 w-4 text-grey-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              id="sched-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full pl-8 pr-2 py-2 border border-grey-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-04 focus:border-transparent"
              disabled={submitting}
              required
            />
          </div>
        </div>

        <div>
          <label htmlFor="sched-time" className="block text-xs font-medium text-grey-600 mb-1">
            Time (Sydney AEST/AEDT)
          </label>
          <div className="relative">
            <Clock className="h-4 w-4 text-grey-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              id="sched-time"
              type="time"
              step={60}
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="w-full pl-8 pr-2 py-2 border border-grey-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-04 focus:border-transparent"
              disabled={submitting}
              required
            />
          </div>
        </div>

        {error && (
          <p className="text-xs text-danger-600 bg-danger-50 border border-danger-200 rounded px-2 py-1">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-1.5 text-xs text-grey-700 border border-grey-200 rounded hover:bg-grey-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-3 py-1.5 text-xs bg-primary-04 text-white rounded hover:bg-primary-600 disabled:opacity-50"
          >
            {submitting ? 'Scheduling…' : 'Schedule'}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Helper for quick-pick buttons (Tomorrow 10am / 2pm). Returns a UTC ISO
 * string representing the requested hour, Sydney TZ, on the next day.
 */
export function tomorrowAtSydney(hourSydney: number, minuteSydney = 0): string {
  const nowSydney = utcToSydneyParts(new Date());
  const [y, mo, d] = nowSydney.date.split('-').map(Number);
  // "Tomorrow" in Sydney terms.
  const nextLocal = new Date(Date.UTC(y, mo - 1, d + 1));
  const tomorrowSydney = utcToSydneyParts(nextLocal).date;
  const hh = String(hourSydney).padStart(2, '0');
  const mm = String(minuteSydney).padStart(2, '0');
  const utc = sydneyWallClockToUtcDate(tomorrowSydney, `${hh}:${mm}`);
  if (!utc) throw new Error('failed to compute tomorrowAtSydney');
  return utc.toISOString();
}

/** Format a UTC ISO string as a Sydney-local short string, e.g. "Tue 10 Jun 10:00 AEST". */
export function formatSydneyShort(utcIso: string | null): string {
  if (!utcIso) return '';
  const d = new Date(utcIso);
  return new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Sydney',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZoneName: 'short',
  }).format(d);
}
